import React from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Radio,
  Typography,
} from "@mui/material";
import { CalendarMonth, CheckCircle, Close, Tune } from "@mui/icons-material";
import moment from "moment";
import AppDateField from "../../../lib/components/AppDateField";
import {
  DATE_PRESETS,
  buildDateParams,
  computePresetRange,
  normalizeDatePreset,
} from "./DateRangePreset";

export const ALL_LOG_METHODS = ["GET", "POST", "PATCH"];
export const ALL_STATUS_CLASSES = ["2xx", "3xx", "4xx", "5xx"];

const METHOD_STYLES = {
  GET: { color: "#6EADFC", bg: "#E1F5FF", border: "#6EADFC", checked: "#6EADFC" },
  POST: { color: "#C4B5FD", bg: "#F5F3FF", border: "#C4B5FD", checked: "#C4B5FD" },
  PATCH: { color: "#FFBC5D", bg: "#FFF4E6", border: "#FFBC5D", checked: "#FFBC5D" },
};

const STATUS_CLASS_OPTIONS = [
  { value: "2xx", label: "Success", color: "#059669", bg: "#ECFDF5", border: "#6EE7B7" },
  { value: "3xx", label: "Redirection", color: "#2563EB", bg: "#EFF6FF", border: "#93C5FD" },
  { value: "4xx", label: "Client Error", color: "#D97706", bg: "#FFFBEB", border: "#FCD34D" },
  { value: "5xx", label: "Server Error", color: "#DC2626", bg: "#FEF2F2", border: "#FCA5A5" },
];

const FAILURE_CATEGORY_OPTIONS = [
  { value: null, label: "All Categories" },
  { value: "VALIDATION", label: "Validation" },
  { value: "UPSTREAM", label: "Upstream" },
];

const RESPONSE_STATUS_OPTIONS = [
  { value: null, label: "All Types" },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILED", label: "Failure" },
];

const DATE_CHIP_PRESETS = [
  DATE_PRESETS.THIS_MONTH,
  DATE_PRESETS.TODAY,
  DATE_PRESETS.LAST_7_DAYS,
  DATE_PRESETS.LAST_30_DAYS,
  DATE_PRESETS.LAST_90_DAYS,
  DATE_PRESETS.THIS_YEAR,
  DATE_PRESETS.ALL_TIME,
];

const ACCENT = "#2563EB";
const LABEL_SX = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: "#6B7280",
  textTransform: "uppercase",
  mb: 0.75,
};

/**
 * @param {string} [datePreset]
 */
export function createDefaultLogFilters(datePreset = DATE_PRESETS.THIS_MONTH) {
  return {
    datePreset: normalizeDatePreset(datePreset),
    dateFrom: null,
    dateTo: null,
    failureCategory: null,
    method: null,
    statusClass: null,
    status: null,
  };
}

/**
 * Count filter dimensions that actually restrict the log list.
 * The section's default date preset is not counted, so an untouched
 * table does not look like filters are already applied.
 * @param {object} filters
 * @param {string} [defaultDatePreset]
 */
export function countActiveLogFilters(filters = {}, defaultDatePreset = DATE_PRESETS.THIS_MONTH) {
  let count = 0;
  const preset = normalizeDatePreset(filters.datePreset);
  const baseline = normalizeDatePreset(defaultDatePreset);
  if (preset === DATE_PRESETS.CUSTOM) {
    if (filters.dateFrom || filters.dateTo) count += 1;
  } else if (preset !== baseline) {
    count += 1;
  }
  if (filters.failureCategory) count += 1;
  if (filters.method) count += 1;
  if (filters.statusClass) count += 1;
  if (filters.status) count += 1;
  return count;
}

/**
 * Map UI filter state onto the request-log query params.
 * method and statusClass are single enum values; omit them when unset.
 * @param {object} filters
 */
export function toLogQueryParams(filters = {}) {
  const params = buildDateParams(filters.datePreset, filters.dateFrom, filters.dateTo);
  if (filters.status) params.status = filters.status;
  if (filters.failureCategory) params.failureCategory = filters.failureCategory;
  if (filters.method) params.method = filters.method;
  if (filters.statusClass) params.statusClass = filters.statusClass;
  return params;
}

function cloneFilters(filters) {
  return {
    ...createDefaultLogFilters(filters?.datePreset),
    ...filters,
    method: filters?.method || null,
    statusClass: filters?.statusClass || null,
  };
}

function toPickerDate(value) {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
}

function formatInclusive(value) {
  if (!value) return null;
  return moment(value).format("YYYY-MM-DD");
}

/**
 * Inclusive from/to dates for the pickers.
 * Named presets derive their range locally; CUSTOM uses stored dates.
 * Backend `dateTo` is exclusive, so preset ends are shifted back one day.
 */
function getInclusiveRange(filters) {
  const preset = normalizeDatePreset(filters?.datePreset);
  if (preset === DATE_PRESETS.CUSTOM) {
    return { from: filters?.dateFrom || null, to: filters?.dateTo ? moment(filters.dateTo).subtract(1, "day").format("YYYY-MM-DD") : null };
  }
  if (preset === DATE_PRESETS.ALL_TIME) {
    return { from: null, to: null };
  }
  const range = computePresetRange(preset);
  return {
    from: formatInclusive(range.dateFrom),
    to: range.dateTo ? moment(range.dateTo).subtract(1, "day").format("YYYY-MM-DD") : null,
  };
}

function dateChipLabel(preset) {
  if (preset === DATE_PRESETS.THIS_MONTH) return "This Month";
  if (preset === DATE_PRESETS.TODAY) return "Today";
  if (preset === DATE_PRESETS.LAST_7_DAYS) return "Last 7 Days";
  if (preset === DATE_PRESETS.LAST_30_DAYS) return "Last 30 Days";
  if (preset === DATE_PRESETS.LAST_90_DAYS) return "Last 90 Days";
  if (preset === DATE_PRESETS.THIS_YEAR) return "This Year";
  if (preset === DATE_PRESETS.ALL_TIME) return "All Time";
  return "Custom";
}

const SectionLabel = ({ children, action }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
    <Typography sx={{ ...LABEL_SX, mb: 0 }}>{children}</Typography>
    {action}
  </Box>
);

/**
 * Filter Logs dropdown anchored to the table Filter button.
 */
const FilterLogsModal = ({
  filters,
  defaultDatePreset = DATE_PRESETS.THIS_MONTH,
  onApply,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [draft, setDraft] = React.useState(() => cloneFilters(filters));
  const appliedCount = countActiveLogFilters(filters, defaultDatePreset);

  React.useEffect(() => {
    if (!open) return;
    setDraft(cloneFilters(filters));
    // Snapshot applied filters only when the menu opens so in-progress edits are not reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const inclusiveRange = getInclusiveRange(draft);
  const draftCount = countActiveLogFilters(draft, defaultDatePreset);
  const isCustomIncomplete =
    normalizeDatePreset(draft.datePreset) === DATE_PRESETS.CUSTOM && (!draft.dateFrom || !draft.dateTo);

  function handleClose() {
    setAnchorEl(null);
  }

  function updateDraft(patch) {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function handlePresetSelect(preset) {
    updateDraft({
      datePreset: preset,
      dateFrom: null,
      dateTo: null,
    });
  }

  function handleDateChange(which, value) {
    const nextDate = value ? moment(value).format("YYYY-MM-DD") : null;
    const current = getInclusiveRange(draft);
    const nextFrom = which === "from" ? nextDate : current.from;
    const nextTo = which === "to" ? nextDate : current.to;
    if (!nextFrom || !nextTo) {
      updateDraft({
        datePreset: DATE_PRESETS.CUSTOM,
        dateFrom: nextFrom,
        dateTo: nextTo ? moment(nextTo).add(1, "day").format("YYYY-MM-DD") : null,
      });
      return;
    }
    updateDraft({
      datePreset: DATE_PRESETS.CUSTOM,
      dateFrom: nextFrom,
      dateTo: moment(nextTo).add(1, "day").format("YYYY-MM-DD"),
    });
  }

  function selectMethod(method) {
    updateDraft({ method: draft.method === method ? null : method });
  }

  function selectStatusClass(value) {
    updateDraft({ statusClass: draft.statusClass === value ? null : value });
  }

  function handleReset() {
    setDraft(createDefaultLogFilters(defaultDatePreset));
  }

  function handleApply() {
    if (isCustomIncomplete) return;
    onApply?.(cloneFilters(draft));
    handleClose();
  }

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        startIcon={<Tune />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "10px",
          px: 2,
          whiteSpace: "nowrap",
          backgroundColor: "#fff",
          borderColor: open ? ACCENT : "#D1D5DB",
          color: "#374151",
          height: 40,
        }}
      >
        Filter
        {appliedCount > 0 ? (
          <Typography
            component="span"
            variant="caption"
            sx={{
              ml: 1,
              px: 0.9,
              py: 0.15,
              borderRadius: "999px",
              bgcolor: "#EEF4FF",
              color: ACCENT,
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {appliedCount}
          </Typography>
        ) : null}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 420,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "min(72vh, 560px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.14)",
          },
        }}
      >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        px={1.75}
        py={1.25}
        borderBottom="1px solid #F3F4F6"
        flexShrink={0}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              bgcolor: "#EEF4FF",
              color: ACCENT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Tune sx={{ fontSize: 16 }} />
          </Box>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flexWrap="wrap">
            <Typography fontSize={14} fontWeight={700} color="#111827">
              Filter Logs
            </Typography>
            {draftCount > 0 && (
              <Box
                component="span"
                sx={{
                  px: 0.8,
                  py: 0.1,
                  borderRadius: "999px",
                  bgcolor: "#EEF4FF",
                  color: ACCENT,
                  fontSize: 10,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {draftCount} Active
              </Box>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose} aria-label="Close filters" sx={{ color: "#9CA3AF", p: 0.5 }}>
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 1.75, py: 1.5, overflow: "auto", flex: 1 }}>
        <Box mb={1.75}>
          <SectionLabel>Date & Time Range</SectionLabel>
          <Box display="flex" flexWrap="wrap" gap={0.75} mb={1}>
            {DATE_CHIP_PRESETS.map((preset) => {
              const selected = normalizeDatePreset(draft.datePreset) === preset;
              return (
                <Button
                  key={preset}
                  onClick={() => handlePresetSelect(preset)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 11,
                    borderRadius: "999px",
                    px: 1.15,
                    py: 0.25,
                    minWidth: 0,
                    minHeight: 26,
                    color: selected ? "#fff" : "#374151",
                    bgcolor: selected ? ACCENT : "#F3F4F6",
                    "&:hover": { bgcolor: selected ? "#1D4ED8" : "#E5E7EB" },
                  }}
                >
                  {dateChipLabel(preset)}
                </Button>
              );
            })}
          </Box>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
            <Box>
              <Typography fontSize={11} color="#6B7280" mb={0.4}>
                From:
              </Typography>
              <AppDateField
                value={toPickerDate(inclusiveRange.from)}
                maxDate={toPickerDate(inclusiveRange.to) || undefined}
                onChange={(value) => handleDateChange("from", value)}
                inputFormat="MMM d, yyyy"
                PopperProps={{ disablePortal: true }}
                InputProps={{
                  startAdornment: <CalendarMonth sx={{ color: "#9CA3AF", fontSize: 16, mr: 0.5 }} />,
                }}
              />
            </Box>
            <Box>
              <Typography fontSize={11} color="#6B7280" mb={0.4}>
                To:
              </Typography>
              <AppDateField
                value={toPickerDate(inclusiveRange.to)}
                minDate={toPickerDate(inclusiveRange.from) || undefined}
                onChange={(value) => handleDateChange("to", value)}
                inputFormat="MMM d, yyyy"
                PopperProps={{ disablePortal: true }}
                InputProps={{
                  startAdornment: <CalendarMonth sx={{ color: "#9CA3AF", fontSize: 16, mr: 0.5 }} />,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box mb={1.75}>
          <SectionLabel>Failure Category</SectionLabel>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75}>
            {FAILURE_CATEGORY_OPTIONS.map((option) => {
              const selected = (draft.failureCategory || null) === option.value;
              return (
                <Box
                  key={option.label}
                  onClick={() => updateDraft({ failureCategory: option.value })}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.4,
                    borderRadius: "8px",
                    border: `1px solid ${selected ? "#93C5FD" : "#E5E7EB"}`,
                    bgcolor: selected ? "#EEF6FF" : "#fff",
                    cursor: "pointer",
                    minHeight: 32,
                  }}
                >
                  <Radio
                    checked={selected}
                    size="small"
                    tabIndex={-1}
                    disableRipple
                    onClick={(event) => event.preventDefault()}
                    sx={{ p: 0.15, color: "#D1D5DB", "&.Mui-checked": { color: ACCENT }, "& .MuiSvgIcon-root": { fontSize: 16 } }}
                  />
                  <Typography fontSize={12} fontWeight={600} color="#111827" noWrap>
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box mb={1.75}>
          <SectionLabel>HTTP Method</SectionLabel>
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {ALL_LOG_METHODS.map((method) => {
              const selected = draft.method === method;
              const tone = METHOD_STYLES[method];
              return (
                <Box
                  key={method}
                  onClick={() => selectMethod(method)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.3,
                    borderRadius: "8px",
                    border: `1.5px solid ${selected ? tone.border : "#E5E7EB"}`,
                    bgcolor: selected ? tone.bg : "#fff",
                    cursor: "pointer",
                  }}
                >
                  <Radio
                    checked={selected}
                    size="small"
                    tabIndex={-1}
                    disableRipple
                    onClick={(event) => event.preventDefault()}
                    sx={{
                      p: 0,
                      color: "#D1D5DB",
                      "&.Mui-checked": { color: tone.checked },
                      "& .MuiSvgIcon-root": { fontSize: 16 },
                    }}
                  />
                  <Typography fontSize={12} fontWeight={700} color={selected ? tone.color : "#6B7280"}>
                    {method}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box mb={1.75}>
          <SectionLabel>HTTP Status Code Range</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={0.75}>
            {STATUS_CLASS_OPTIONS.map((option) => {
              const selected = draft.statusClass === option.value;
              return (
                <Box
                  key={option.value}
                  onClick={() => selectStatusClass(option.value)}
                  sx={{
                    position: "relative",
                    px: 1,
                    py: 0.85,
                    borderRadius: "10px",
                    border: `1.5px solid ${option.border}`,
                    bgcolor: option.bg,
                    cursor: "pointer",
                    opacity: !draft.statusClass || selected ? 1 : 0.55,
                  }}
                >
                  {selected && (
                    <CheckCircle
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        fontSize: 13,
                        color: option.color,
                      }}
                    />
                  )}
                  <Typography fontSize={13} fontWeight={800} color={option.color} lineHeight={1.2}>
                    {option.value}
                  </Typography>
                  <Typography fontSize={10} fontWeight={600} color={option.color} sx={{ opacity: 0.85 }} noWrap>
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box>
          <SectionLabel>Response Status</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={0.75}>
            {RESPONSE_STATUS_OPTIONS.map((option) => {
              const selected = (draft.status || null) === option.value;
              return (
                <Button
                  key={option.label}
                  onClick={() => updateDraft({ status: option.value })}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12,
                    borderRadius: "8px",
                    py: 0.65,
                    minHeight: 32,
                    color: selected ? ACCENT : "#374151",
                    bgcolor: selected ? "#EEF6FF" : "#fff",
                    border: `1.5px solid ${selected ? "#93C5FD" : "#E5E7EB"}`,
                    "&:hover": { bgcolor: selected ? "#EEF6FF" : "#F9FAFB", borderColor: selected ? "#93C5FD" : "#D1D5DB" },
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          px: 1.75,
          py: 1.15,
          borderTop: "1px solid #F3F4F6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <Button
          size="small"
          onClick={handleReset}
          sx={{ textTransform: "none", fontWeight: 600, color: "#6B7280", minWidth: 0, fontSize: 12 }}
        >
          Reset
        </Button>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Button
            size="small"
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              borderColor: "#D1D5DB",
              color: "#374151",
              px: 1.5,
              fontSize: 12,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleApply}
            disabled={isCustomIncomplete}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              bgcolor: ACCENT,
              px: 1.5,
              fontSize: 12,
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
      </Popover>
    </>
  );
};

export default FilterLogsModal;
